/**
 * Motor de reconocimiento facial del navegador (cámara del propio equipo).
 *
 * La cara se analiza **en el navegador**: lo único que sale de aquí es el *descriptor* (un vector
 * de 1024 números que resume la cara de forma irreversible). La foto nunca se envía ni se guarda,
 * así que el servidor no almacena datos biométricos crudos.
 *
 * El modelo se descarga desde la propia web (`/models`, copiados del paquete de modelos) y se carga
 * de forma diferida: la librería pesa, y quien entre con contraseña no debe pagarla.
 *
 * Requisito de navegador: la cámara sólo funciona en *contexto seguro* (HTTPS o `localhost`).
 * Si el panel se abre por IP en HTTP, `navigator.mediaDevices` no existe y se avisa con claridad.
 */

import type { Human } from '@vladmandic/human';

/** Identificador del modelo: los descriptores de modelos distintos no se pueden comparar. */
export const MODELO_ROSTRO = 'human-faceres-1024';

/** Confianza mínima de que lo que hay delante es una persona real (no una foto). */
const VIVACIDAD_MINIMA = Number(import.meta.env.VITE_BIOMETRIA_VIVACIDAD ?? 0.35);

/** Tamaño mínimo/máximo de la cara respecto al ancho del vídeo. */
const ANCHO_MINIMO = 0.16;
const ANCHO_MAXIMO = 0.8;

/** Desvío máximo del centro del encuadre. */
const DESVIO_CENTRO = 0.2;

/** Ángulo máximo (grados) para considerar que la cara mira de frente. */
const ANGULO_MAXIMO = 25;

/** Cuántas lecturas seguidas correctas hacen falta para capturar sola. */
export const LECTURAS_ESTABLES = 3;

export interface RostroDetectado {
  /** Vector de la cara (1024 números). Es lo único que viaja al servidor. */
  descriptor: number[];
  /** Confianza del detector (0..1). */
  puntaje: number;
  /** Confianza de que es una persona real y no una foto (0..1). */
  vivacidad: number;
  /** Ancho de la cara respecto al vídeo (0..1). */
  ancho: number;
}

export interface Encuadre {
  /** `true` cuando la captura es válida y se puede guardar. */
  listo: boolean;
  /** Qué corregir cuando todavía no está listo (se muestra al usuario). */
  aviso: string | null;
  /** Cuántas caras hay delante. */
  caras: number;
  rostro: RostroDetectado | null;
}

/** ¿La política del documento permite la cámara? `null` si el navegador no expone la API. */
function politicaPermiteCamara(): boolean | null {
  const policy = (
    document as Document & { featurePolicy?: { allowsFeature?: (feature: string) => boolean } }
  ).featurePolicy;

  if (!policy?.allowsFeature) return null;

  try {
    return policy.allowsFeature('camera');
  } catch {
    return null;
  }
}

/** Estado del permiso de cámara según el navegador (`null` si no se puede consultar). */
async function estadoPermisoCamara(): Promise<PermissionState | null> {
  try {
    const permiso = await navigator.permissions.query({ name: 'camera' as PermissionName });
    return permiso.state;
  } catch {
    return null;
  }
}

/** ¿Se puede usar la cámara en este navegador/contexto? */
export function revisarSoporte(): { ok: boolean; motivo?: string; corto?: string } {
  if (typeof window === 'undefined') {
    return { ok: false, motivo: 'No hay navegador disponible.' };
  }

  if (!window.isSecureContext) {
    return {
      ok: false,
      motivo:
        'La cámara necesita una conexión segura (HTTPS o entrar por localhost). Pide al administrador publicar el panel con HTTPS.',
      corto: 'La cámara necesita HTTPS o entrar por localhost.',
    };
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      ok: false,
      motivo: 'Este navegador no permite usar la cámara.',
      corto: 'Este navegador no permite usar la cámara.',
    };
  }

  // Algunas ventanas integradas (vistas previas dentro de otro programa) bloquean la cámara por
  // política: en ese caso el navegador no llega ni a pedir el permiso.
  if (politicaPermiteCamara() === false) {
    return {
      ok: false,
      motivo:
        'El navegador tiene bloqueado el acceso a la cámara para esta página (política del sitio o vista previa integrada). Abre el panel en una ventana del navegador y vuelve a intentarlo.',
      corto: 'Abre el panel en el navegador: aquí la cámara está bloqueada.',
    };
  }

  return { ok: true };
}

// ------------------------------------------------------------------
// Motor (se carga una sola vez, bajo demanda)
// ------------------------------------------------------------------

let motor: Promise<Human> | null = null;

/**
 * Carga la librería y los modelos (la primera vez tarda unos segundos).
 *
 * Se importa de forma dinámica para que el paquete no entre en el bundle inicial: sólo lo paga
 * quien va a usar la cámara.
 */
export function prepararMotor(): Promise<Human> {
  if (motor) return motor;

  motor = (async () => {
    const { Human } = await import('@vladmandic/human');

    const instancia = new Human({
      // Los modelos se sirven desde la propia web: sin depender de CDN externos.
      modelBasePath: `${import.meta.env.BASE_URL}models/`,
      backend: 'webgl',
      cacheModels: true,
      face: {
        enabled: true,
        detector: {
          modelPath: 'blazeface.json',
          // Se admiten dos caras para poder avisar de que sólo debe salir una persona.
          maxDetected: 2,
          minConfidence: 0.3,
          rotation: false,
        },
        description: {
          enabled: true,
          modelPath: 'faceres.json',
          // El descriptor se recalcula en cada lectura: se compara con el de la captura.
          skipFrames: 0,
          skipTime: 0,
        },
        // La malla permite avisar si el usuario no mira de frente.
        mesh: { enabled: true, modelPath: 'facemesh.json' },
        iris: { enabled: false },
        emotion: { enabled: false },
        // Antispoof y vivacidad: descartan fotos o pantallas delante de la cámara.
        antispoof: { enabled: true, modelPath: 'antispoof.json', skipFrames: 10, skipTime: 1500 },
        liveness: { enabled: true, modelPath: 'liveness.json', skipFrames: 10, skipTime: 1500 },
      },
      // El resto de módulos no se usan y desactivados no se descargan.
      body: { enabled: false },
      hand: { enabled: false },
      gesture: { enabled: false },
      object: { enabled: false },
      segmentation: { enabled: false },
      filter: { enabled: false },
    });

    await instancia.load();
    await instancia.warmup();

    return instancia;
  })();

  // Si falla, se permite reintentar (por ejemplo tras dar permiso a la cámara).
  motor.catch(() => {
    motor = null;
  });

  return motor;
}

/** Libera el motor (al cerrar la cámara). */
export function liberarMotor(): void {
  motor = null;
}

// ------------------------------------------------------------------
// Cámara
// ------------------------------------------------------------------

let camara: MediaStream | null = null;

/** Pide la cámara y la conecta al elemento de vídeo. */
export async function abrirCamara(video: HTMLVideoElement): Promise<MediaStream> {
  const soporte = revisarSoporte();

  if (!soporte.ok) throw new Error(soporte.motivo);

  cerrarCamara(video);

  try {
    // Cámara frontal del equipo en uso: en un celular o tablet, la de selfie.
    camara = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 640 },
        height: { ideal: 480 },
        frameRate: { ideal: 24, max: 30 },
      },
      audio: false,
    });
  } catch (error) {
    const nombre = (error as DOMException)?.name;

    if (nombre === 'OverconstrainedError' || nombre === 'TypeError') {
      // Algunos móviles no aceptan la resolución pedida: se reintenta con la cámara por defecto.
      try {
        camara = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      } catch {
        throw new Error('No se encontró ninguna cámara utilizable en este equipo.');
      }
    } else if (nombre === 'NotAllowedError' || nombre === 'SecurityError') {
      const permiso = await estadoPermisoCamara();

      throw new Error(
        permiso === 'denied'
          ? 'El navegador tiene bloqueado el acceso a la cámara. Habilítalo desde el candado de la barra de direcciones (o abre el panel en una ventana del navegador, no en una vista previa integrada) y vuelve a intentarlo.'
          : 'No se autorizó el uso de la cámara. Acepta «Permitir» cuando el navegador lo pida y vuelve a intentarlo.',
      );
    } else if (nombre === 'NotFoundError') {
      throw new Error('No se encontró ninguna cámara conectada a este equipo.');
    } else {
      throw new Error('No se pudo abrir la cámara de este equipo.');
    }
  }

  video.srcObject = camara;
  video.muted = true;
  video.playsInline = true;

  await video.play().catch(() => undefined);

  return camara;
}

/** Apaga la cámara y desconecta el vídeo. */
export function cerrarCamara(video?: HTMLVideoElement | null): void {
  if (camara) {
    camara.getTracks().forEach((pista) => pista.stop());
    camara = null;
  }

  if (video) {
    video.pause();
    video.srcObject = null;
  }
}

/** ¿Hay una cámara encendida? */
export function camaraActiva(): boolean {
  return Boolean(camara?.getVideoTracks().some((pista) => pista.readyState === 'live'));
}

// ------------------------------------------------------------------
// Lectura de un fotograma
// ------------------------------------------------------------------

/**
 * Analiza el fotograma actual y dice si la cara se puede usar.
 *
 * Devuelve siempre un motivo concreto cuando todavía no vale, para que en pantalla se indique qué
 * hacer ("acércate", "mira de frente"…) en lugar de un error genérico.
 */
export async function analizarFotograma(video: HTMLVideoElement): Promise<Encuadre> {
  if (!video.videoWidth) {
    // Sin imagen todavía: el vídeo aún no ha arrancado.
    return { listo: false, aviso: 'Esperando a la cámara…', caras: 0, rostro: null };
  }

  const human = await prepararMotor();
  const resultado = await human.detect(video);

  const caras = resultado.face ?? [];

  if (caras.length === 0) {
    return { listo: false, aviso: 'Colócate frente a la cámara.', caras: 0, rostro: null };
  }

  if (caras.length > 1) {
    return {
      listo: false,
      aviso: 'Hay más de una persona en el encuadre: sólo debe salir una.',
      caras: caras.length,
      rostro: null,
    };
  }

  const [cara] = caras;

  if (!cara) {
    return { listo: false, aviso: 'Colócate frente a la cámara.', caras: 0, rostro: null };
  }

  const anchoVideo = video.videoWidth || 1;
  const ancho = (cara.box?.[2] ?? 0) / anchoVideo;
  const centroX = (cara.boxRaw?.[0] ?? 0) + (cara.boxRaw?.[2] ?? 0) / 2;
  const centroY = (cara.boxRaw?.[1] ?? 0) + (cara.boxRaw?.[3] ?? 0) / 2;
  const angulo = cara.rotation?.angle;

  const rostro: RostroDetectado = {
    descriptor: (cara.embedding ?? []).map(Number),
    puntaje: Number(cara.boxScore ?? cara.score ?? 0),
    vivacidad: Number(cara.real ?? cara.live ?? 0),
    ancho,
  };

  if (!rostro.descriptor.length) {
    return { listo: false, aviso: 'No se pudo leer tu cara. Inténtalo otra vez.', caras: 1, rostro: null };
  }

  if (ancho < ANCHO_MINIMO) {
    return { listo: false, aviso: 'Acércate un poco a la cámara.', caras: 1, rostro };
  }

  if (ancho > ANCHO_MAXIMO) {
    return { listo: false, aviso: 'Aléjate un poco de la cámara.', caras: 1, rostro };
  }

  if (Math.abs(centroX - 0.5) > DESVIO_CENTRO || Math.abs(centroY - 0.5) > DESVIO_CENTRO) {
    return { listo: false, aviso: 'Centra tu cara en el óvalo.', caras: 1, rostro };
  }

  if (angulo) {
    const desviado =
      Math.abs(Number(angulo.yaw ?? 0)) > ANGULO_MAXIMO ||
      Math.abs(Number(angulo.pitch ?? 0)) > ANGULO_MAXIMO ||
      Math.abs(Number(angulo.roll ?? 0)) > ANGULO_MAXIMO;

    if (desviado) {
      return { listo: false, aviso: 'Mira de frente a la cámara.', caras: 1, rostro };
    }
  }

  if (rostro.vivacidad && rostro.vivacidad < VIVACIDAD_MINIMA) {
    return {
      listo: false,
      aviso: 'No se pudo confirmar que eres una persona real (¿una foto o una pantalla?).',
      caras: 1,
      rostro,
    };
  }

  return { listo: true, aviso: null, caras: 1, rostro };
}

/** Redondea el descriptor para no enviar ceros de más (la comparación no pierde precisión útil). */
export function redondearDescriptor(descriptor: number[]): number[] {
  return descriptor.map((valor) => Number(valor.toFixed(6)));
}
